// utils/searchStorage.jsx - Enhanced with Privacy Controls & Tier Management
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Storage keys
const STORAGE_KEYS = {
  SEARCHES: 'coderated_searches',
  PUBLIC_FEED: 'coderated_public_feed',
  USER_SEARCHES: 'coderated_user_searches',
  PRIVACY_SETTINGS: 'coderated_privacy_settings'
};

// Enhanced storage utility with better error handling
const isSSR = () => typeof window === 'undefined';

const StorageUtil = {
  get: (key, defaultValue = null) => {
    try {
      if (isSSR()) return defaultValue;
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading from localStorage (${key}):`, error);
      return defaultValue;
    }
  },

  set: (key, value) => {
    try {
      if (isSSR()) return false;
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`Error writing to localStorage (${key}):`, error);
      return false;
    }
  },

  remove: (key) => {
    try {
      if (isSSR()) return false;
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing from localStorage (${key}):`, error);
      return false;
    }
  }
};

// Create search context
const SearchContext = createContext();

// Enhanced search storage hook with privacy controls
export const SearchProvider = ({ children }) => {
  const [searches, setSearches] = useState([]);
  const [publicFeed, setPublicFeed] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [privacySettings, setPrivacySettings] = useState({});

  // Load data from localStorage on init
  useEffect(() => {
    console.log('🔄 Loading search data from localStorage...');
    
    const loadedSearches = StorageUtil.get(STORAGE_KEYS.SEARCHES, []);
    const loadedPublicFeed = StorageUtil.get(STORAGE_KEYS.PUBLIC_FEED, []);
    const loadedPrivacySettings = StorageUtil.get(STORAGE_KEYS.PRIVACY_SETTINGS, {});
    
    console.log('📊 Loaded searches:', loadedSearches.length);
    console.log('📊 Loaded public feed:', loadedPublicFeed.length);
    console.log('🔒 Loaded privacy settings:', loadedPrivacySettings);
    
    setSearches(loadedSearches);
    setPublicFeed(loadedPublicFeed);
    setPrivacySettings(loadedPrivacySettings);
    setIsLoading(false);
  }, []);

  // Save searches to localStorage whenever searches change
  useEffect(() => {
    if (!isLoading && searches.length > 0) {
      console.log('💾 Saving searches to localStorage:', searches.length);
      StorageUtil.set(STORAGE_KEYS.SEARCHES, searches);
    }
  }, [searches, isLoading]);

  // Save public feed to localStorage whenever it changes
  useEffect(() => {
    if (!isLoading && publicFeed.length > 0) {
      console.log('💾 Saving public feed to localStorage:', publicFeed.length);
      StorageUtil.set(STORAGE_KEYS.PUBLIC_FEED, publicFeed);
    }
  }, [publicFeed, isLoading]);

  // Save privacy settings to localStorage
  useEffect(() => {
    if (!isLoading) {
      StorageUtil.set(STORAGE_KEYS.PRIVACY_SETTINGS, privacySettings);
    }
  }, [privacySettings, isLoading]);

  // 🔒 PRIVACY-AWARE: Add search with explicit privacy control
  const addSearch = useCallback((searchData) => {
    try {
      console.log('➕ Adding new search:', searchData.url);
      
      const enhancedSearch = {
        ...searchData,
        id: searchData.id || `search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: searchData.timestamp || new Date().toISOString(),
        // 🔒 PRIVACY FIRST: Only public if explicitly set to true
        isPublic: searchData.isPublic === true,
        source: searchData.demo_mode ? 'demo' : 'analysis',
        tier: searchData.tier || 'free',
        privacyRequested: searchData.privacyRequested || false
      };

      // Add to searches
      setSearches(prev => {
        // Remove any existing search with same URL to avoid duplicates
        const filtered = prev.filter(s => s.url !== enhancedSearch.url);
        const updated = [enhancedSearch, ...filtered];
        console.log('✅ Search added to searches list. Total:', updated.length);
        return updated;
      });

      // 🔒 ONLY add to public feed if explicitly marked as public
      if (enhancedSearch.isPublic === true) {
        console.log('📢 Adding to public feed (user chose public)');
        addToPublicFeed(enhancedSearch);
      } else {
        console.log('🔒 Keeping private (user chose private or not specified)');
      }

      return enhancedSearch.id;
    } catch (error) {
      console.error('❌ Error adding search:', error);
      return null;
    }
  }, []);

  // 🛡️ PRIVACY-VALIDATED: Add to public feed with double-check
  const addToPublicFeed = useCallback((searchData) => {
    try {
      // 🛡️ Double-check privacy before adding to public feed
      if (searchData.isPublic !== true) {
        console.log('🔒 Blocked: Attempted to add private analysis to public feed');
        return null;
      }
      
      console.log('📢 Adding to public feed:', searchData.url);
      
      const publicItem = {
        ...searchData,
        id: searchData.id || `public_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: searchData.timestamp || new Date().toISOString(),
        isPublic: true, // Ensure it's marked as public
        addedToFeedAt: new Date().toISOString(),
        tier: searchData.tier || 'free'
      };

      setPublicFeed(prev => {
        // Remove any existing item with same URL to avoid duplicates
        const filtered = prev.filter(item => item.url !== publicItem.url);
        const updated = [publicItem, ...filtered].slice(0, 100); // Keep only latest 100
        console.log('✅ Item added to public feed. Total:', updated.length);
        return updated;
      });

      return publicItem.id;
    } catch (error) {
      console.error('❌ Error adding to public feed:', error);
      return null;
    }
  }, []);

  // Get all searches
  const getSearches = useCallback(() => {
    console.log('📋 Getting all searches:', searches.length);
    return searches;
  }, [searches]);

  // Get all searches (alias)
  const getAllSearches = useCallback(() => {
    return getSearches();
  }, [getSearches]);

  // Get public feed
  const getPublicFeed = useCallback(() => {
    console.log('📋 Getting public feed:', publicFeed.length);
    return publicFeed;
  }, [publicFeed]);

  // 🔒 NEW: Get user's private searches only
  const getPrivateSearches = useCallback(() => {
    const privateSearches = searches.filter(search => search.isPublic !== true);
    console.log('🔒 Getting private searches:', privateSearches.length);
    return privateSearches;
  }, [searches]);

  // 📢 NEW: Get user's public searches only
  const getPublicSearches = useCallback(() => {
    const publicSearches = searches.filter(search => search.isPublic === true);
    console.log('📢 Getting public searches:', publicSearches.length);
    return publicSearches;
  }, [searches]);

  // Get search by ID
  const getSearchById = useCallback((id) => {
    return searches.find(search => search.id === id) || null;
  }, [searches]);

  // Get search by URL
  const getSearchByUrl = useCallback((url) => {
    const normalizedUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
    return searches.find(search => {
      const searchUrl = search.url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
      return searchUrl === normalizedUrl;
    }) || null;
  }, [searches]);

  // Get recent searches
  const getRecentSearches = useCallback((limit = 10) => {
    return searches.slice(0, limit);
  }, [searches]);

  // Update search (privacy-aware)
  const updateSearch = useCallback((id, updateData) => {
    try {
      let searchBeforeUpdate = null;
      
      setSearches(prev => {
        const updated = prev.map(search => {
          if (search.id === id) {
            searchBeforeUpdate = search;
            return { ...search, ...updateData, updatedAt: new Date().toISOString() };
          }
          return search;
        });
        console.log('✅ Search updated:', id);
        return updated;
      });

      // Handle privacy changes in public feed
      const updatedSearch = { ...searchBeforeUpdate, ...updateData };
      
      if (searchBeforeUpdate && searchBeforeUpdate.isPublic) {
        // Was public before
        if (updatedSearch.isPublic === true) {
          // Still public - update in feed
          setPublicFeed(prev => {
            return prev.map(item => 
              item.id === id 
                ? { ...item, ...updateData, updatedAt: new Date().toISOString() }
                : item
            );
          });
        } else {
          // Made private - remove from feed
          setPublicFeed(prev => prev.filter(item => item.id !== id));
          console.log('🔒 Removed from public feed (made private)');
        }
      } else if (updatedSearch.isPublic === true) {
        // Wasn't public before but is now - add to feed
        addToPublicFeed(updatedSearch);
      }

      return true;
    } catch (error) {
      console.error('❌ Error updating search:', error);
      return false;
    }
  }, [addToPublicFeed]);

  // Delete search
  const deleteSearch = useCallback((id) => {
    try {
      setSearches(prev => {
        const updated = prev.filter(search => search.id !== id);
        console.log('🗑️ Search deleted:', id);
        return updated;
      });

      // Also remove from public feed
      setPublicFeed(prev => {
        const updated = prev.filter(item => item.id !== id);
        return updated;
      });

      return true;
    } catch (error) {
      console.error('❌ Error deleting search:', error);
      return false;
    }
  }, []);

  // Clear all searches
  const clearSearches = useCallback(() => {
    try {
      setSearches([]);
      setPublicFeed([]);
      StorageUtil.remove(STORAGE_KEYS.SEARCHES);
      StorageUtil.remove(STORAGE_KEYS.PUBLIC_FEED);
      console.log('🧹 All searches cleared');
      return true;
    } catch (error) {
      console.error('❌ Error clearing searches:', error);
      return false;
    }
  }, []);

  // 🔒 NEW: Privacy Settings Management
  const updatePrivacySettings = useCallback((settings) => {
    try {
      setPrivacySettings(prev => ({
        ...prev,
        ...settings,
        updatedAt: new Date().toISOString()
      }));
      console.log('🔒 Privacy settings updated:', settings);
      return true;
    } catch (error) {
      console.error('❌ Error updating privacy settings:', error);
      return false;
    }
  }, []);

  const getPrivacySettings = useCallback(() => {
    return privacySettings;
  }, [privacySettings]);

  // Export searches
  const exportSearches = useCallback(() => {
    try {
      const exportData = {
        searches: searches,
        publicFeed: publicFeed,
        privacySettings: privacySettings,
        exportedAt: new Date().toISOString(),
        version: '2.0' // Updated version for privacy features
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coderated-searches-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('📥 Searches exported successfully');
      return true;
    } catch (error) {
      console.error('❌ Error exporting searches:', error);
      return false;
    }
  }, [searches, publicFeed, privacySettings]);

  // Import searches
  const importSearches = useCallback((importData) => {
    try {
      if (importData.searches) {
        setSearches(importData.searches);
      }
      if (importData.publicFeed) {
        setPublicFeed(importData.publicFeed);
      }
      if (importData.privacySettings) {
        setPrivacySettings(importData.privacySettings);
      }
      console.log('📤 Searches imported successfully');
      return true;
    } catch (error) {
      console.error('❌ Error importing searches:', error);
      return false;
    }
  }, []);

  // 📊 ENHANCED: Get search statistics with privacy breakdown
  const getSearchStats = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000));
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const todaySearches = searches.filter(s => new Date(s.timestamp) >= today);
    const weekSearches = searches.filter(s => new Date(s.timestamp) >= thisWeek);
    const monthSearches = searches.filter(s => new Date(s.timestamp) >= thisMonth);

    const publicSearches = searches.filter(s => s.isPublic === true);
    const privateSearches = searches.filter(s => s.isPublic !== true);

    const averageScore = searches.length > 0 
      ? searches.reduce((sum, s) => sum + (s.score || 0), 0) / searches.length 
      : 0;

    return {
      total: searches.length,
      today: todaySearches.length,
      thisWeek: weekSearches.length,
      thisMonth: monthSearches.length,
      averageScore: Math.round(averageScore),
      publicItems: publicFeed.length,
      // 🔒 Privacy breakdown
      publicSearches: publicSearches.length,
      privateSearches: privateSearches.length,
      publicPercentage: searches.length > 0 ? Math.round((publicSearches.length / searches.length) * 100) : 0,
      privacyChoicesMade: searches.filter(s => s.privacyRequested === true).length
    };
  }, [searches, publicFeed]);

  // 🔒 NEW: Get privacy analytics
  const getPrivacyStats = useCallback(() => {
    const publicCount = searches.filter(s => s.isPublic === true).length;
    const privateCount = searches.filter(s => s.isPublic !== true).length;
    const tierBreakdown = searches.reduce((acc, search) => {
      const tier = search.tier || 'free';
      acc[tier] = (acc[tier] || 0) + 1;
      return acc;
    }, {});

    return {
      totalSearches: searches.length,
      publicSearches: publicCount,
      privateSearches: privateCount,
      publicPercentage: searches.length > 0 ? Math.round((publicCount / searches.length) * 100) : 0,
      tierBreakdown,
      privacyFeatureUsage: searches.filter(s => s.privacyRequested === true).length
    };
  }, [searches]);

  // 🔒 NEW: Check if user can make more private searches (for free tier limits)
  const canMakePrivateSearch = useCallback((userTier = 'free') => {
    if (userTier === 'enterprise' || userTier === 'pro') {
      return { canUse: true, reason: 'unlimited' };
    }
    
    // Free tier: limit to 3 private searches per month
    if (userTier === 'free') {
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const privateThisMonth = searches.filter(s => 
        s.isPublic !== true && new Date(s.timestamp) >= thisMonth
      ).length;
      
      const limit = 3;
      const remaining = Math.max(0, limit - privateThisMonth);
      
      return {
        canUse: remaining > 0,
        remaining,
        limit,
        used: privateThisMonth,
        reason: remaining > 0 ? 'within_limit' : 'monthly_limit_reached'
      };
    }
    
    return { canUse: true, reason: 'unknown_tier' };
  }, [searches]);

  const contextValue = {
    // Data
    searches,
    publicFeed,
    isLoading,
    privacySettings,
    
    // Original Methods
    addSearch,
    addToPublicFeed,
    getSearches,
    getAllSearches,
    getPublicFeed,
    getSearchById,
    getSearchByUrl,
    getRecentSearches,
    updateSearch,
    deleteSearch,
    clearSearches,
    exportSearches,
    importSearches,
    getSearchStats,
    
    // 🔒 NEW: Privacy Methods
    getPrivateSearches,
    getPublicSearches,
    updatePrivacySettings,
    getPrivacySettings,
    getPrivacyStats,
    canMakePrivateSearch,
    
    // Aliases for compatibility
    getAll: getSearches,
    getPublic: getPublicFeed,
    getPrivate: getPrivateSearches
  };

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};

// Hook to use search context
export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    console.warn('useSearch must be used within a SearchProvider');
    return {
      search: {
        searches: [],
        publicFeed: [],
        isLoading: false,
        privacySettings: {},
        addSearch: () => console.warn('SearchProvider not found'),
        addToPublicFeed: () => console.warn('SearchProvider not found'),
        getSearches: () => [],
        getAllSearches: () => [],
        getPublicFeed: () => [],
        getPrivateSearches: () => [],
        getPublicSearches: () => [],
        getSearchById: () => null,
        getSearchByUrl: () => null,
        getRecentSearches: () => [],
        updateSearch: () => false,
        deleteSearch: () => false,
        clearSearches: () => false,
        exportSearches: () => false,
        importSearches: () => false,
        getSearchStats: () => ({}),
        updatePrivacySettings: () => false,
        getPrivacySettings: () => ({}),
        getPrivacyStats: () => ({}),
        canMakePrivateSearch: () => ({ canUse: true, reason: 'no_provider' }),
        getAll: () => [],
        getPublic: () => [],
        getPrivate: () => []
      }
    };
  }
  
  return { search: context };
};

// Default export for convenience
export default { SearchProvider, useSearch };